import { withNamespaces } from "react-i18next";
import { Text, View } from "@react-pdf/renderer";
import styles from "../../Styles";

const CorporateLinkages = ({ t, company, company_shareholders = [] }) => {

  const pickByType = (type) =>
    (company_shareholders ?? []).find((x) => x?.companyType === type);
  
  const normalize = (x) =>
    x
      ? {
          name: x.name ?? "-",
          duns: x.duns ?? "-",
          address: x.address ?? "-",
          city: x.city ?? "-",
          country: x.country ?? "-",
        }
      : null;
  
  // Prefer ultimateShareholderData, fallback to company_shareholders
  const domestic =
    company?.ultimateShareholderData?.domesticUltimate ??
    normalize(pickByType("domestic"));
  
  const global =
    company?.ultimateShareholderData?.globalUltimate ??
    normalize(pickByType("global"));
  
  // Headquarters from shareholders fallback (or from a future HQ object)
  const hq = pickByType("headquarters");
  

  const ultimateRows = [
    {
      detail: "Name",
      domestic: domestic?.name ?? "-",
      global: global?.name ?? "-",
    },
    {
      detail: "D-U-N-S",
      domestic: domestic?.duns ?? "-",
      global: global?.duns ?? "-",
    },
    {
      detail: "Address",
      domestic: domestic?.address ?? "-",
      global: global?.address ?? "-",
    },
    {
      detail: "City, Country",
      domestic:
        [domestic?.city, domestic?.country].filter(Boolean).join(", ") || "-",
      global: [global?.city, global?.country].filter(Boolean).join(", ") || "-",
    },
  ];

  const headquartersRows = [
    { title: "Headquarters Name", details: hq?.name ?? "-" },
    { title: "Headquarters D-U-N-S", details: hq?.duns ?? "-" },
    {
      title: "Headquarters Address",
      details:
        [
          hq?.address,
          [hq?.postcode, hq?.city].filter(Boolean).join(" "),
          hq?.country,
        ]
          .filter(Boolean)
          .join(", ") || "-",
    },
    {
      title: "Total Family Members (Subsidiaries/Affiliates)",
      details: company?.familyMemberCount ?? "-",
    },
  ];
  

  return (
    <View style={styles.section} break>
      {/* Section title */}
      <Text style={styles.sectionSeparator}>
        {`${t("6.")} ${t("Corporate Linkages (Family Tree)")}`}
      </Text>

      {/* 6.1 table: Detail | Domestic Ultimate | Global Ultimate */}
      <View style={styles.table} wrap={false}>
        {/* header row */}
        <View style={styles.tableRow} wrap={false} minPresenceAhead={24}>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Detail")}
            </Text>
          </View>

          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Domestic Ultimate")}
            </Text>
          </View>

          <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Global Ultimate")}
            </Text>
          </View>
        </View>

        {/* body rows */}
        {ultimateRows.map((r, i) => {
          const rowStyle = {
            ...styles.tableRow,
            ...(i === ultimateRows.length - 1 ? styles.tableLastRow : {}),
          };

          return (
            <View style={rowStyle} key={i} wrap={false}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{t(r.detail)}</Text>
              </View>

              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{r.domestic ?? "-"}</Text>
              </View>

              <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
                <Text style={styles.tableCell}>{r.global ?? "-"}</Text>
              </View>
            </View>
          );
        })}
      </View>

      {/* 6.2 Headquarters */}
      <Text style={styles.alphListItem}>{`6.2 ${t("Headquarters")}`}</Text>

      <View style={styles.table} wrap={false}>
        {/* header row */}
        <View style={styles.tableRow} wrap={false} minPresenceAhead={24}>
          <View style={styles.tableColHeader}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Title")}
            </Text>
          </View>

          <View style={{ ...styles.tableColHeader, ...styles.borderRightNone }}>
            <Text style={{ ...styles.tableCell, ...styles.fontBold }}>
              {t("Details")}
            </Text>
          </View>
        </View>

        {/* body rows */}
        {headquartersRows.map((d, i) => {
          const rowStyle = {
            ...styles.tableRow,
            ...(i === headquartersRows.length - 1 ? styles.tableLastRow : {}),
          };

          return (
            <View style={rowStyle} key={i} wrap={false}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{t(d.title)}</Text>
              </View>

              <View style={{ ...styles.tableCol, ...styles.borderRightNone }}>
                <Text style={styles.tableCell}>{d.details ?? "-"}</Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default withNamespaces()(CorporateLinkages);
